import { CompRef } from '@wix/document-services-types';
import {
  EditActionsDictionary,
  NativeActionsList,
} from '@wix/editor-platform-host-integration-apis';
import { ComponentsPlatformService } from '@wix/editor-platform-manifest-services';

import { isSingleSelection } from '../../utils';
import globalDesignActions from '../globalDesign/actions';

type EditActionValidator = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) => boolean;

import {
  validatePresetsAction,
  validateRichTextAction,
  validateSettingsAction,
  validateHelpAction,
  validateInteractionAction,
  validateDesignAction,
  validateDesignStatesAction,
  validateManageMenuAction,
  validateManageItemsAction,
  validateMediaAction,
  validateEditImageAction,
  validatePluginsAction,
  validateGlobalColorsStylesAction,
  validateCropImageAction,
  validateOpacityAction,
} from './common';

import type { EditorPointer } from '@wix/editor-document-logic-api';

export const editActionsValidators: EditActionsDictionary<EditActionValidator> =
  {
    [NativeActionsList.presets]: validatePresetsAction,
    [NativeActionsList.link]: (componentsAPI, editorPointers) =>
      componentsAPI.editActions.hasActionableDataItems(
        NativeActionsList.link,
        editorPointers,
      ),
    [NativeActionsList.editText]: (componentsAPI, editorPointers) =>
      componentsAPI.editActions.hasActionableDataItems(
        NativeActionsList.editText,
        editorPointers,
      ),
    [NativeActionsList.editRichText]: validateRichTextAction,
    [NativeActionsList.richTextAlignment]: validateRichTextAction,
    [NativeActionsList.richTextBold]: validateRichTextAction,
    [NativeActionsList.richTextDirection]: validateRichTextAction,
    [NativeActionsList.richTextFontFamily]: validateRichTextAction,
    [NativeActionsList.richTextFontSize]: validateRichTextAction,
    [NativeActionsList.richTextForeColor]: validateRichTextAction,
    [NativeActionsList.richTextItalic]: validateRichTextAction,
    [NativeActionsList.richTextLink]: validateRichTextAction,
    [NativeActionsList.richTextPanel]: validateRichTextAction,
    [NativeActionsList.richTextStrikethrough]: validateRichTextAction,
    [NativeActionsList.richTextUnderline]: validateRichTextAction,
    [NativeActionsList.pin]: (componentsAPI, editorPointers) =>
      !componentsAPI.layout.getValue(editorPointers, 'disablePositioning'),
    // Native Actions validators
    [NativeActionsList.settings]: validateSettingsAction,
    [NativeActionsList.help]: validateHelpAction,
    [NativeActionsList.interactions]: validateInteractionAction,
    [NativeActionsList.design]: validateDesignAction,
    [NativeActionsList.designStates]: validateDesignStatesAction,
    [NativeActionsList.manageMenu]: validateManageMenuAction,
    [NativeActionsList.manageItems]: validateManageItemsAction,
    [NativeActionsList.changeMedia]: validateMediaAction,
    [NativeActionsList.editImage]: validateEditImageAction,
    [NativeActionsList.opacity]: validateOpacityAction,
    [NativeActionsList.cropImage]: validateCropImageAction,
    [NativeActionsList.adjustImage]: validateEditImageAction,
    [NativeActionsList.filterImage]: validateEditImageAction,
    [NativeActionsList.elements]: (componentsAPI, editorPointers) =>
      isSingleSelection(editorPointers)
        ? componentsAPI.panels.shouldShowElementsPanel(editorPointers[0])
        : false,
    [NativeActionsList.plugins]: validatePluginsAction,
    // Global design actions validators
    [NativeActionsList.textStyles]:
      globalDesignActions[NativeActionsList.textStyles].validator,
    [NativeActionsList.cornerStyles]:
      globalDesignActions[NativeActionsList.cornerStyles].validator,
    [NativeActionsList.fillStyles]:
      // Disable fill styles for now
      () => false,
    [NativeActionsList.spacingStyles]:
      globalDesignActions[NativeActionsList.spacingStyles].validator,
    [NativeActionsList.globalColorsStyles]: validateGlobalColorsStylesAction,
    [NativeActionsList.borderStyles]:
      globalDesignActions[NativeActionsList.borderStyles].validator,
    [NativeActionsList.shadowStyles]:
      globalDesignActions[NativeActionsList.shadowStyles].validator,
    [NativeActionsList.strokeStyles]:
      globalDesignActions[NativeActionsList.strokeStyles].validator,
    [NativeActionsList.filterStyles]:
      globalDesignActions[NativeActionsList.filterStyles].validator,
  };
